import React, { useState, useEffect } from 'react';
import { Copy, Check, Clock, User, Link as LinkIcon } from 'lucide-react';
import Button from '../common/Button/Button';
import Card from '../common/Card/Card';
import { TOKEN_STORAGE_KEY } from '../../utils/auth';

interface TrialInvite {
  id: number;
  plan_name: string;
  credits_grant: number;
  expires_at: string | null;
  used_at: string | null;
  used_by_user_id: number | null;
  used_by_email: string | null;
  created_at: string;
}

interface TrialUser {
  id: number;
  email: string;
  username: string;
  created_at: string | null;
}

const TrialInvitePanel: React.FC = () => {
  const [invites, setInvites] = useState<TrialInvite[]>([]);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastGeneratedUrl, setLastGeneratedUrl] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [trialUsers, setTrialUsers] = useState<TrialUser[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [recipientEmail, setRecipientEmail] = useState('');
  const [recipientName, setRecipientName] = useState('');
  const [emailSent, setEmailSent] = useState(false);

  const apiUrl = process.env.REACT_APP_API_URL || '';
  const token = localStorage.getItem(TOKEN_STORAGE_KEY);
  const headers = {
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json'
  };

  const fetchInvites = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${apiUrl}/api/admin/trials/invites?limit=10`, { headers });
      if (response.ok) {
        const data = await response.json();
        setInvites(data.invites || []);
      } else {
        console.error('Failed to fetch trial invites');
      }
    } catch (err) {
      console.error('Error fetching invites:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchTrialUsers = async () => {
    setLoadingUsers(true);
    try {
      const response = await fetch(`${apiUrl}/api/admin/trials/users`, { headers });
      if (response.ok) {
        const data = await response.json();
        setTrialUsers(data.users || []);
      } else {
        console.error('Failed to fetch trial users');
      }
    } catch (err) {
      console.error('Error fetching trial users:', err);
    } finally {
      setLoadingUsers(false);
    }
  };

  useEffect(() => {
    fetchInvites();
    fetchTrialUsers();
  }, []);

  const generateTrialLink = async () => {
    setGenerating(true);
    setError(null);
    setLastGeneratedUrl(null);
    setEmailSent(false);
    try {
      const response = await fetch(`${apiUrl}/api/admin/trials/invites`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          recipient_email: recipientEmail || null,
          recipient_name: recipientName || null,
        }),
      });

      if (response.ok) {
        const data = await response.json();
        setLastGeneratedUrl(data.invite_url);
        setEmailSent(data.email_sent === true);
        fetchInvites(); // Refresh list
      } else {
        const data = await response.json();
        setError(data.detail || 'Failed to generate trial link');
      }
    } catch (err) {
      setError('Error connecting to server');
      console.error(err);
    } finally {
      setGenerating(false);
    }
  };

  const copyToClipboard = () => {
    if (lastGeneratedUrl) {
      navigator.clipboard.writeText(lastGeneratedUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <Card className="overflow-hidden">
      <div className="space-y-6">
        <div>
          <h3 className="text-lg font-medium text-gray-900 flex items-center gap-2">
            <LinkIcon className="h-5 w-5 text-primary" />
            Generate Trial Link
          </h3>
          <p className="text-sm text-gray-500 mt-1">
            Creates a single-use 5-day Growth trial link (no card required)
          </p>
        </div>

        <div className="flex flex-col sm:flex-row gap-3">
          <input
            type="text"
            placeholder="Name (optional)"
            value={recipientName}
            onChange={(e) => setRecipientName(e.target.value)}
            className="flex-1 border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-primary"
          />
          <input
            type="email"
            placeholder="Prospect email (optional)"
            value={recipientEmail}
            onChange={(e) => setRecipientEmail(e.target.value)}
            className="flex-1 border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-primary"
          />
          <Button
            onClick={generateTrialLink}
            isLoading={generating}
            disabled={generating}
            className="whitespace-nowrap"
          >
            {recipientEmail ? 'Generate & Send Email' : 'Generate Link'}
          </Button>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md text-sm">
            {error}
          </div>
        )}

        {emailSent && recipientEmail && (
          <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-md text-sm">
            Email sent to {recipientEmail}
          </div>
        )}

        {lastGeneratedUrl && (
          <div className="bg-primary/5 border border-primary/20 rounded-lg p-4">
            <label className="block text-xs font-semibold text-primary uppercase tracking-wider mb-2">
              New Trial Link (Single Use)
            </label>
            <div className="flex items-stretch gap-2">
              <input
                type="text"
                readOnly
                value={lastGeneratedUrl}
                className="flex-1 bg-white border border-gray-300 rounded-md px-3 py-2 text-sm font-mono text-gray-700 focus:outline-none focus:ring-1 focus:ring-primary"
              />
              <button
                onClick={copyToClipboard}
                className="inline-flex items-center justify-center px-4 py-2 border border-primary bg-primary text-white rounded-md hover:bg-primary/90 transition-colors"
                title="Copy to clipboard"
              >
                {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
              </button>
            </div>
            <p className="text-xs text-gray-500 mt-2 italic">
              Share this URL with the prospect. It will expire in 30 days if not used.
            </p>
          </div>
        )}

        <div className="pt-4 border-t border-gray-100">
          <h4 className="text-sm font-semibold text-gray-700 mb-4 flex items-center gap-2">
            <Clock className="h-4 w-4" />
            Recent Invites
          </h4>
          
          {loading && invites.length === 0 ? (
            <div className="animate-pulse space-y-2">
              <div className="h-8 bg-gray-100 rounded"></div>
              <div className="h-8 bg-gray-100 rounded"></div>
            </div>
          ) : invites.length === 0 ? (
            <p className="text-xs text-gray-500 italic py-2">No recent invites found.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Created</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Redeemed By</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {invites.map((invite) => (
                    <tr key={invite.id}>
                      <td className="px-3 py-2 whitespace-nowrap text-xs text-gray-600">
                        {new Date(invite.created_at).toLocaleDateString()}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap">
                        {invite.used_at ? (
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                            Used
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                            Pending
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap text-xs text-gray-600">
                        {invite.used_by_email ? (
                          <div className="flex items-center gap-1">
                            <User className="h-3 w-3" />
                            {invite.used_by_email}
                          </div>
                        ) : (
                          <span className="text-gray-400">—</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="pt-4 border-t border-gray-100">
          <h4 className="text-sm font-semibold text-gray-700 mb-4 flex items-center gap-2">
            <User className="h-4 w-4" />
            Trial Registered Users
          </h4>

          {loadingUsers && trialUsers.length === 0 ? (
            <div className="animate-pulse space-y-2">
              <div className="h-8 bg-gray-100 rounded"></div>
              <div className="h-8 bg-gray-100 rounded"></div>
            </div>
          ) : trialUsers.length === 0 ? (
            <p className="text-xs text-gray-500 italic py-2">No trial users yet.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Username</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Registered</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {trialUsers.map((user) => (
                    <tr key={user.id}>
                      <td className="px-3 py-2 whitespace-nowrap text-xs text-gray-600">{user.email}</td>
                      <td className="px-3 py-2 whitespace-nowrap text-xs text-gray-600">{user.username}</td>
                      <td className="px-3 py-2 whitespace-nowrap text-xs text-gray-600">
                        {user.created_at ? new Date(user.created_at).toLocaleDateString() : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </Card>
  );
};

export default TrialInvitePanel;
